import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { bindActionCreators } from "redux";
import { useDispatch } from "react-redux";
import {
    AppStoreMonitorState,
    CompleteApplePayload,
    GetDashboardActionPayload,
    GetIssueActionPayload,
    IAppStoreCheckRunAttributes,
    IAppStoreDashboardAttributes,
    IAppStoreIssueAttributes,
    NeedsCorrectionApplePayload,
    RetrieveAppleKeyPayload,
    RunCheckActionPayload,
    SendAlertActionPayload,
} from "../types/app-store-monitor";
import { IAppleSetupQueueItem } from "../types/apple-setup";

const initialState: AppStoreMonitorState = {
    getDashboard: {
        data: undefined,
        loading: false,
        success: false,
        error: false,
        forbidden: false,
    },
    getIssue: {
        data: undefined,
        loading: false,
        success: false,
        error: false,
        id: null,
    },
    runCheck: {
        data: undefined,
        loading: false,
        success: false,
        error: false,
    },
    sendAlert: {
        data: undefined,
        loading: false,
        success: false,
        error: false,
    },
    getAppleQueue: {
        items: [],
        loading: false,
        success: false,
        error: false,
        forbidden: false,
    },
    completeApple: { loading: false, success: false, error: false },
    needsCorrectionApple: { loading: false, success: false, error: false },
    retrieveAppleKey: { loading: false, success: false, error: false },
};

const appStoreMonitorSlice = createSlice({
    name: "appStoreMonitor",
    initialState,
    reducers: {
        getDashboard(state, _action: GetDashboardActionPayload) {
            state.getDashboard = {
                data: state.getDashboard.data,
                loading: true,
                success: false,
                error: false,
                forbidden: false,
            };
        },
        getDashboardSuccess(
            state,
            action: PayloadAction<IAppStoreDashboardAttributes>
        ) {
            state.getDashboard = {
                data: action.payload,
                loading: false,
                success: true,
                error: false,
                forbidden: false,
            };
        },
        getDashboardFailure(
            state,
            action: PayloadAction<{ forbidden: boolean }>
        ) {
            state.getDashboard = {
                data: undefined,
                loading: false,
                success: false,
                error: true,
                forbidden: action.payload?.forbidden ?? false,
            };
        },

        getIssue(state, action: GetIssueActionPayload) {
            state.getIssue = {
                data: undefined,
                loading: true,
                success: false,
                error: false,
                id: action.payload,
            };
        },
        getIssueSuccess(
            state,
            action: PayloadAction<{
                id: number;
                attributes: IAppStoreIssueAttributes;
            }>
        ) {
            // Ignore responses for an issue that is no longer the one on screen
            // (modal closed, or a different issue was opened mid-flight).
            if (state.getIssue.id !== action.payload.id) {
                return;
            }
            state.getIssue = {
                data: action.payload.attributes,
                loading: false,
                success: true,
                error: false,
                id: state.getIssue.id,
            };
        },
        getIssueFailure(state, action: PayloadAction<{ id: number }>) {
            if (state.getIssue.id !== action.payload.id) {
                return;
            }
            state.getIssue = {
                data: undefined,
                loading: false,
                success: false,
                error: true,
                id: state.getIssue.id,
            };
        },
        clearIssue(state) {
            state.getIssue = {
                data: undefined,
                loading: false,
                success: false,
                error: false,
                id: null,
            };
        },

        runCheck(state, _action: RunCheckActionPayload) {
            state.runCheck = {
                data: undefined,
                loading: true,
                success: false,
                error: false,
            };
        },
        runCheckSuccess(
            state,
            action: PayloadAction<IAppStoreCheckRunAttributes>
        ) {
            state.runCheck = {
                data: action.payload,
                loading: false,
                success: true,
                error: false,
            };
        },
        runCheckFailure(state) {
            state.runCheck = {
                data: undefined,
                loading: false,
                success: false,
                error: true,
            };
        },
        clearRunCheck(state) {
            state.runCheck = {
                data: undefined,
                loading: false,
                success: false,
                error: false,
            };
        },

        sendAlert(state, _action: SendAlertActionPayload) {
            state.sendAlert = {
                data: undefined,
                loading: true,
                success: false,
                error: false,
            };
        },
        sendAlertSuccess(
            state,
            action: PayloadAction<{
                id: number;
                attributes: IAppStoreIssueAttributes;
            }>
        ) {
            state.sendAlert = {
                data: action.payload.attributes,
                loading: false,
                success: true,
                error: false,
            };
            // Only refresh the open issue if the alert was for the issue still
            // on screen — otherwise we'd show one issue's data under another's id.
            if (state.getIssue.id === action.payload.id) {
                state.getIssue = {
                    data: action.payload.attributes,
                    loading: false,
                    success: true,
                    error: false,
                    id: state.getIssue.id,
                };
            }
        },
        sendAlertFailure(state) {
            state.sendAlert = {
                data: undefined,
                loading: false,
                success: false,
                error: true,
            };
        },
        clearSendAlert(state) {
            state.sendAlert = {
                data: undefined,
                loading: false,
                success: false,
                error: false,
            };
        },

        getAppleQueue(state) {
            state.getAppleQueue = {
                items: state.getAppleQueue.items,
                loading: true,
                success: false,
                error: false,
                forbidden: false,
            };
        },
        getAppleQueueSuccess(
            state,
            action: PayloadAction<IAppleSetupQueueItem[]>
        ) {
            state.getAppleQueue = {
                items: action.payload,
                loading: false,
                success: true,
                error: false,
                forbidden: false,
            };
        },
        getAppleQueueFailure(
            state,
            action: PayloadAction<{ forbidden: boolean }>
        ) {
            state.getAppleQueue = {
                items: [],
                loading: false,
                success: false,
                error: true,
                forbidden: action.payload?.forbidden ?? false,
            };
        },

        completeApple(state, _action: PayloadAction<CompleteApplePayload>) {
            state.completeApple = { loading: true, success: false, error: false };
        },
        completeAppleSuccess(state) {
            state.completeApple = { loading: false, success: true, error: false };
        },
        completeAppleFailure(state) {
            state.completeApple = { loading: false, success: false, error: true };
        },

        needsCorrectionApple(
            state,
            _action: PayloadAction<NeedsCorrectionApplePayload>
        ) {
            state.needsCorrectionApple = {
                loading: true,
                success: false,
                error: false,
            };
        },
        needsCorrectionAppleSuccess(state) {
            state.needsCorrectionApple = {
                loading: false,
                success: true,
                error: false,
            };
        },
        needsCorrectionAppleFailure(state) {
            state.needsCorrectionApple = {
                loading: false,
                success: false,
                error: true,
            };
        },

        retrieveAppleKey(
            state,
            _action: PayloadAction<RetrieveAppleKeyPayload>
        ) {
            state.retrieveAppleKey = { loading: true, success: false, error: false };
        },
        retrieveAppleKeySuccess(state) {
            state.retrieveAppleKey = { loading: false, success: true, error: false };
        },
        retrieveAppleKeyFailure(state) {
            state.retrieveAppleKey = { loading: false, success: false, error: true };
        },
        clearAppleAction(state) {
            state.completeApple = { loading: false, success: false, error: false };
            state.needsCorrectionApple = {
                loading: false,
                success: false,
                error: false,
            };
            state.retrieveAppleKey = { loading: false, success: false, error: false };
        },
    },
});

export const {
    getDashboard,
    getDashboardSuccess,
    getDashboardFailure,
    getIssue,
    getIssueSuccess,
    getIssueFailure,
    clearIssue,
    runCheck,
    runCheckSuccess,
    runCheckFailure,
    clearRunCheck,
    sendAlert,
    sendAlertSuccess,
    sendAlertFailure,
    clearSendAlert,
    getAppleQueue,
    getAppleQueueSuccess,
    getAppleQueueFailure,
    completeApple,
    completeAppleSuccess,
    completeAppleFailure,
    needsCorrectionApple,
    needsCorrectionAppleSuccess,
    needsCorrectionAppleFailure,
    retrieveAppleKey,
    retrieveAppleKeySuccess,
    retrieveAppleKeyFailure,
    clearAppleAction,
} = appStoreMonitorSlice.actions;

export const mapDispatchToProps = () => {
    return bindActionCreators(
        {
            getDashboard,
            getIssue,
            clearIssue,
            runCheck,
            clearRunCheck,
            sendAlert,
            clearSendAlert,
            getAppleQueue,
            completeApple,
            needsCorrectionApple,
            retrieveAppleKey,
            clearAppleAction,
        },
        useDispatch()
    );
};

export default appStoreMonitorSlice.reducer;
